import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  ModalBackdrop,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  HStack,
  Text,
  Icon,
  Button,
  ButtonText,
  ButtonIcon,
} from '@gluestack-ui/themed';
import { X, Save, Trash2 } from 'lucide-react-native';
import { useDialog } from '@/common/context/DialogContext';
import { QuestionEditor, type MCQQuestion } from './QuestionEditor';

interface QuestionEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  moduleId: string;
  version: string;
  taskId: string;
  question: MCQQuestion;
  onSave: (question: MCQQuestion) => void;
  onDelete: (questionId: string) => void;
  isReadOnly?: boolean;
}

export const QuestionEditorModal: React.FC<QuestionEditorModalProps> = ({
  isOpen,
  onClose,
  moduleId,
  version,
  taskId,
  question,
  onSave,
  onDelete,
  isReadOnly = false,
}) => {
  const { showDialog } = useDialog();
  const { t } = useTranslation();
  const editorRef = React.useRef<{ getQuestion:() => MCQQuestion; hasChanges: () => boolean } | null>(null);

  const showConfirmDialog = (title: string, message: string, onConfirm: () => void) => {
    showDialog({
      title,
      message,
      buttons: [
        { text: t('common.buttons.cancel'), variant: 'outline', action: 'secondary' },
        {
          text: title.toLowerCase().includes('delete') ? t('common.buttons.delete') : t('common.buttons.ok'),
          action: title.toLowerCase().includes('delete') ? 'negative' : 'primary',
          onPress: onConfirm,
        },
      ],
    });
  };

  const handleClose = () => {
    if (editorRef.current?.hasChanges()) {
      showConfirmDialog(
        t('lecturer.moduleEditor.moduleEditor.dialogs.unsavedChanges.title'),
        t('lecturer.moduleEditor.moduleEditor.dialogs.unsavedChanges.message'),
        onClose,
      );
    } else {
      onClose();
    }
  };

  const handleDelete = () => {
    showConfirmDialog(
      t('lecturer.moduleEditor.taskEditor.mcq.deleteQuestionTitle'),
      t('lecturer.moduleEditor.taskEditor.mcq.deleteQuestionMessage'),
      () => { onDelete(question.id); onClose(); },
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="full">
      <ModalBackdrop />
      <ModalContent
        maxWidth={720}
        w="95%"
        maxHeight="85%"
        bg="$white"
        borderRadius="$lg"
        softShadow="3"
      >
        <ModalHeader
          bg="$white"
          borderBottomWidth={1}
          borderBottomColor="$borderLight"
          px="$4"
          py="$3"
        >
          <Text size="sm" fontWeight="semibold" color="$textHeading">
            {t('lecturer.moduleEditor.taskEditor.mcq.editQuestion')}
          </Text>
          <ModalCloseButton p="$2" m="-$2">
            <Icon as={X} size="sm" color="$textSubtle" />
          </ModalCloseButton>
        </ModalHeader>

        <ModalBody p="$0" flex={1} bg="$white">
          <QuestionEditor
            ref={editorRef}
            moduleId={moduleId}
            version={version}
            taskId={taskId}
            question={question}
            isReadOnly={isReadOnly}
          />
        </ModalBody>

        {!isReadOnly && (
          <ModalFooter
            bg="$white"
            borderTopWidth={1}
            borderTopColor="$borderLight"
            px="$4"
            py="$3"
          >
            <HStack flex={1} justifyContent="space-between" alignItems="center">
              <Button
                testID="question-editor-delete-button"
                action="negative"
                variant="link"
                size="sm"
                onPress={handleDelete}
              >
                <ButtonIcon as={Trash2} size="xs" mr="$2" />
                <ButtonText>Delete</ButtonText>
              </Button>

              <HStack space="sm">
                <Button
                  testID="question-editor-cancel-button"
                  variant="outline"
                  size="sm"
                  onPress={handleClose}
                >
                  <ButtonText>Cancel</ButtonText>
                </Button>
                <Button
                  testID="question-editor-save-button"
                  size="sm"
                  onPress={() => {
                    const q = editorRef.current?.getQuestion();
                    if (q) { onSave(q); onClose(); }
                  }}
                >
                  <ButtonIcon as={Save} size="xs" mr="$2" />
                  <ButtonText>Save</ButtonText>
                </Button>
              </HStack>
            </HStack>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};
